import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Icon from './AppIcon';
import Select from './ui/Select';
import propertyService from '../services/propertyService';
import { useAuth } from '../contexts/AuthContext';

const StageBadge = ({ stage, propertyId, onStageChange, variant = 'default' }) => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const badgeRef = useRef(null);
  const menuRef = useRef(null);

  const stages = [
    { value: 'analysis', label: 'Analyse', color: 'var(--color-muted-foreground)' },
    { value: 'visit', label: 'Visite', color: 'var(--color-warning)' },
    { value: 'offer_made', label: 'Offre faite', color: 'var(--color-accent)' },
    { value: 'offer_accepted', label: 'Offre acceptée', color: 'var(--color-primary)' },
    { value: 'acquired', label: 'Propriété acquise', color: 'var(--color-success)' }
  ];

  const currentStage = stages.find(s => s.value === stage) || stages[0];

  const handleStageChange = async (newStage) => {
    if (!propertyId || !user?.id || newStage === stage) {
      setIsOpen(false);
      return;
    }

    try {
      // If parent provides a callback, delegate the update to them
      // This avoids redundant DB updates and race conditions
      if (onStageChange) {
        // IMPORTANT: The parent wrappers expect either (newStage) or (propertyId, newStage)
        // MainDashboard.handleStageChange(propertyId, newStage)
        // AnalysisResults.handleStageChange(propertyId, newStage)
        // We pass both to stay compatible
        await onStageChange(propertyId, newStage);
        setIsOpen(false);
        return;
      }

      // Fallback for standalone usage (not used in current dashboard/results pages)
      const result = await propertyService.getById(propertyId);
      if (!result?.success) {
        throw new Error('Erreur lors du chargement de la propriété');
      }

      const currentFormData = propertyService.toFormFormat(result.data);

      const updateResult = await propertyService.update(propertyId, {
        ...currentFormData,
        stage: newStage
      }, user.id);

      if (!updateResult?.success) {
        throw new Error(updateResult?.error || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Error updating property stage:', error);
      alert(`Erreur lors de la mise à jour: ${error?.message}`);
    } finally {
      setIsOpen(false);
    }
  };

  // Calculate menu position when opening
  useEffect(() => {
    if (isOpen && badgeRef.current && variant === 'card') {
      const rect = badgeRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.top - 4, // Position above the badge
        left: rect.right - 180, // Align to right edge
        right: window.innerWidth - rect.right
      });
    }
  }, [isOpen, variant]);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (badgeRef.current && !badgeRef.current.contains(event.target) &&
        menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  if (variant === 'card') {
    const menuContent = isOpen && (
      <div
        ref={menuRef}
        style={{
          position: 'fixed',
          top: `${menuPosition.top}px`,
          left: menuPosition.right < 0 ? `${menuPosition.left}px` : 'auto',
          right: menuPosition.right >= 0 ? `${menuPosition.right}px` : 'auto',
          background: 'var(--color-card)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-lg)',
          zIndex: 9999,
          minWidth: '180px',
          padding: '4px',
          maxHeight: '200px',
          overflowY: 'auto',
          transform: menuPosition.top < 200 ? 'translateY(calc(100% + 4px))' : 'translateY(-100%)'
        }}
      >
        {stages.map(s => (
          <button
            key={s.value}
            onClick={() => handleStageChange(s.value)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px',
              background: s.value === stage ? 'var(--color-muted)' : 'transparent',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
              fontFamily: 'var(--font-caption)',
              fontSize: '0.875rem',
              color: 'var(--color-foreground)',
              textAlign: 'left'
            }}
            onMouseEnter={(e) => {
              if (s.value !== stage) e.currentTarget.style.background = 'var(--color-muted)';
            }}
            onMouseLeave={(e) => {
              if (s.value !== stage) e.currentTarget.style.background = 'transparent';
            }}
          >
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: s.color
            }} />
            <span>{s.label}</span>
            {s.value === stage && <Icon name="Check" size={14} style={{ marginLeft: 'auto' }} />}
          </button>
        ))}
      </div>
    );

    return (
      <div style={{ position: 'relative' }} ref={badgeRef}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-card)',
            backdropFilter: 'blur(8px)',
            cursor: 'pointer',
            border: `1px solid ${currentStage.color}40`
          }}
          onClick={() => setIsOpen(!isOpen)}
        >
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: currentStage.color
          }} />
          <span style={{
            fontFamily: 'var(--font-caption)',
            fontSize: '0.75rem',
            fontWeight: 600,
            color: 'var(--color-foreground)'
          }}>
            {currentStage.label}
          </span>
          <Icon name="ChevronDown" size={12} className="ml-1" />
        </div>
        {typeof document !== 'undefined' && createPortal(menuContent, document.body)}
      </div>
    );
  }

  return (
    <span
      className="caption font-medium px-3 py-1 rounded-full"
      style={{
        background: `${currentStage.color}15`,
        color: currentStage.color
      }}
    >
      {currentStage.label}
    </span>
  );
};

export default StageBadge;
